import { takeLatest, put, call, takeEvery, select, all } from 'redux-saga/effects';
import { message } from 'antd';
import axios from 'axios';
import { get } from 'lodash';
import getMetricsData from '@api/metricContainer';
import * as actions from './actions';
import * as types from './types';
import * as helpers from './helper';
import * as schema from './schema';
import metricDataSelector from './selectors';
import * as baseTypes from '../base/types';
import {
  intervalSelector,
  filterSelector,
  filterColorSelector,
  selectedKPIConfigSelector,
} from '../base/selectors';

import { granularity as getGranularity } from '@utils';
import humanizeValue from '@utils/humanizeUtil';

function* initRequest() {
  yield put(actions.init());
}

// initialize and create a metric API state
function* initMetric(action) {
  const { metric } = action;
  const metricStore = schema.createMetric(metric);
  yield put(actions.setupMetric({ metric, metricStore }));
  yield fetchMetricData(metric);
}

// fetch metric data
function* fetchMetricData(metric) {
  yield put(actions.setFetching({ metric, isFetching: true }));
  let interval = yield select(intervalSelector);
  const granularity = getGranularity({
    from: interval.from,
    to: interval.to,
    minuteGranularity: true,
    overview: true,
  });
  const filter = yield select(filterSelector);
  interval = `${interval.from}/${interval.to}`;
  const kpiConfig = yield select(selectedKPIConfigSelector);

  const payload = yield helpers.createQuery(metric, interval, filter.rule, granularity, kpiConfig);

  try {
    const source = axios.CancelToken.source();
    const response = yield call(getMetricsData, payload, source);
    const responseData = get(response, 'data[0]', null);

    yield put(
      actions.setResponse({
        metric,
        response: responseData,
      }),
    );

    const filterColor = yield select(filterColorSelector);
    const chartData = yield helpers.formatMetricDataForChart(
      responseData.data,
      metric,
      filterColor,
    );
    const totalValue = responseData.totals[metric];
    const metricValue = humanizeValue(totalValue, metric);

    yield put(actions.setChartData({ metric, chartData }));
    yield put(actions.setTotalValue({ metric, metricValue }));

    yield put(actions.setFetching({ metric, isFetching: false }));
    yield put(actions.setError({ metric, errorMsg: '', errorCode: 0 }));
  } catch (error) {
    const errorMsg = error.message || 'Unknown error!';
    const errorCode = error.code || '';
    message.error(errorMsg);
    yield put(actions.setError({ metric, errorMsg, errorCode }));
    yield put(actions.setFetching({ metric, isFetching: false }));
  }
}

function* watchInitRequest() {
  yield takeLatest([types.INIT], initRequest);
}

function* watchInitMetric() {
  yield takeEvery(types.INIT_METRIC, initMetric);
}

function* refreshMetricData() {
  const metricStore = yield select(metricDataSelector);
  const tasks = Object.keys(metricStore).map((metricKey) => {
    return call(fetchMetricData, metricKey);
  });

  yield all(tasks);
}

function* watchSelectedInterval() {
  yield takeLatest(
    [baseTypes.SET_INTERVAL, baseTypes.APPLY_FILTER, baseTypes.SET_SELECTED_KPI_CONFIG],
    refreshMetricData,
  );
}

export default [watchInitRequest, watchInitMetric, watchSelectedInterval];
